<template>
  <div class="signature-wrapper">
    <div class="card">
      <div class="toolbar">
        <div class="left-controls">
          <div class="color-section">
            <span class="label">Color:</span>
            <div class="color-palette">
              <button
                v-for="c in colors"
                :key="c"
                :style="{ backgroundColor: c }"
                :class="['color-dot', { selected: color === c }]"
                @click="setColor(c)"
              >
                <Check v-if="color === c" :size="14" class="tick" />
              </button>
            </div>
          </div>
          <div class="divider"></div>
          <div class="brush-section" v-if="mode === 'draw'">
            <span class="label">Pen Size:</span>
            <input
              v-model.number="penSize"
              type="range"
              min="1"
              max="10"
              step="1"
              class="slider"
            />
            <span class="size-value">{{ penSize }} px</span>
          </div>
          <div class="font-size-controls" v-if="mode === 'type'">
            <span class="label">Font Size:</span>
            <input
              v-model.number="fontSize"
              type="range"
              min="32"
              max="120"
              step="4"
              class="slider"
            />
            <span class="size-value">{{ fontSize }} px</span>
          </div>
        </div>

        <div class="mode-toggle">
          <button
            :class="['mode-btn', { active: mode === 'draw' }]"
            @click="setMode('draw')"
          >
            <Pen :size="18" class="icon-inline" /> Draw
          </button>
          <button
            :class="['mode-btn', { active: mode === 'type' }]"
            @click="setMode('type')"
          >
            <Type :size="18" class="icon-inline" /> Type
          </button>
        </div>

        <div class="left-controls">
          <button class="icon-btn" @click="undo" :disabled="!canUndo" title="Undo">
            <RotateCcw :size="20" />
          </button>
          <button class="icon-btn" @click="redo" :disabled="!canRedo" title="Redo">
            <RotateCw :size="20" />
          </button>
          <button class="icon-btn danger" @click="clearPad" title="Clear">
            <Trash2 :size="20" />
          </button>
        </div>
      </div>

      <div class="editor-area">
        <div v-if="mode === 'draw'" class="draw-area">
          <canvas
            ref="drawCanvas"
            class="draw-canvas"
            @mousedown="startDraw"
            @mousemove="draw"
            @mouseup="endDraw"
            @mouseleave="endDraw"
            @touchstart.prevent="startDraw"
            @touchmove.prevent="draw"
            @touchend.prevent="endDraw"
          ></canvas>
          <div class="bottom-line"></div>
        </div>
        <div v-else class="type-area">
          <input
            v-model="typedText"
            type="text"
            class="type-input"
            placeholder="Enter your name..."
          />
          <div class="font-preview">
            <span
              :style="{
                fontFamily: fonts[selectedFontIndex].cssName,
                fontSize: fontSize + 'px',
                color: color,
              }"
            >
              {{ typedText || 'Your Name' }}
            </span>
          </div>
          <div class="font-grid">
            <div
              v-for="(font, i) in fonts"
              :key="i"
              :class="['font-card', { selected: selectedFontIndex === i }]"
              @click="selectFont(i)"
            >
              <span
                class="font-sample"
                :style="{ fontFamily: font.cssName, fontSize: '22px' }"
              >
                {{ font.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <!-- Show these buttons only when NOT embedded in PdfEditor -->
        <template v-if="!isEmbedded">
          <button class="btn btn-secondary" @click="navigateToPdfEditor">
            <FileText :size="18" />
            Sign PDF Document
          </button>
          <button class="btn btn-primary" @click="downloadSignature">
            <Download :size="18" />
            Download Signature
          </button>
        </template>
        
        <!-- Show this button only when embedded in PdfEditor -->
        <button v-if="isEmbedded" class="btn btn-primary" @click="useSignature">
          <PenTool :size="18" />
          Use this Signature
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import html2canvas from 'html2canvas'
import {
  Pen,
  Type,
  RotateCcw,
  RotateCw,
  Trash2,
  Check,
  Download,
  FileText,
  PenTool,
} from 'lucide-vue-next'

// Props
const props = defineProps({
  isEmbedded: {
    type: Boolean,
    default: false
  },
  existingSignature: {
    type: [String, Object],
    default: null
  }
})

// Emits
const emit = defineEmits(['signed', 'navigate-to-editor'])

const mode = ref('draw')
const color = ref('#000000')
const penSize = ref(2.5)
const drawCanvas = ref(null)
const isDrawing = ref(false)
const lastPoint = ref({ x: 0, y: 0 })
const ctx = ref(null)

const typedText = ref('')
const fontSize = ref(40)
const selectedFontIndex = ref(0)

const fonts = [
  { name: 'Great Vibes', cssName: "'Great Vibes', cursive", import: 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap' },
  { name: 'Pacifico', cssName: "'Pacifico', cursive", import: 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap' },
  { name: 'Dancing Script', cssName: "'Dancing Script', cursive", import: 'https://fonts.googleapis.com/css2?family=Dancing+Script&display=swap' },
  { name: 'Satisfy', cssName: "'Satisfy', cursive", import: 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap' },
  { name: 'Courgette', cssName: "'Courgette', cursive", import: 'https://fonts.googleapis.com/css2?family=Courgette&display=swap' },
  { name: 'Amatic SC', cssName: "'Amatic SC', cursive", import: 'https://fonts.googleapis.com/css2?family=Amatic+SC&display=swap' },
]

fonts.forEach((f) => {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = f.import
  document.head.appendChild(link)
})

// History stack for undo/redo as image data URLs
const history = reactive({
  stack: [],
  pointer: -1,
})
const maxHistory = 50

const canUndo = computed(() => history.pointer > 0)
const canRedo = computed(() => history.pointer < history.stack.length - 1)

const colors = ['#000000', '#2ecc71', '#dc2626', 'skyblue', '#9333ea', '#ea580c']

function setColor(c) {
  color.value = c
}

function setMode(m) {
  if (mode.value === m) return
  mode.value = m
  if (m === 'draw') {
    // Initialize canvas for drawing
    setTimeout(() => {
      initCanvas()
    }, 50)
  }
}

// Initialize canvas context and size
function initCanvas() {
  const canvas = drawCanvas.value
  if (!canvas) return
  // Set canvas size (could be made responsive)
  const rect = canvas.getBoundingClientRect()
  canvas.width = rect.width * window.devicePixelRatio
  canvas.height = rect.height * window.devicePixelRatio
  canvas.style.width = rect.width + 'px'
  canvas.style.height = rect.height + 'px'
  const context = canvas.getContext('2d')
  context.scale(window.devicePixelRatio, window.devicePixelRatio)
  context.lineCap = 'round'
  context.lineJoin = 'round'
  ctx.value = context

  // Restore last state if any
  if (history.pointer >= 0) {
    restoreHistory(history.pointer)
  } else {
    clearCanvas()
    pushHistory()
  }
}

function clearCanvas() {
  if (ctx.value && drawCanvas.value) {
    ctx.value.clearRect(0, 0, drawCanvas.value.width, drawCanvas.value.height)
  }
}

function pushHistory() {
  if (!drawCanvas.value) return
  const dataURL = drawCanvas.value.toDataURL('image/png')
  if (history.pointer < history.stack.length - 1) {
    history.stack.splice(history.pointer + 1)
  }
  history.stack.push(dataURL)
  if (history.stack.length > maxHistory) {
    history.stack.shift()
  } else {
    history.pointer++
  }
}

function restoreHistory(index) {
  if (!drawCanvas.value || !ctx.value) return
  const img = new Image()
  img.onload = () => {
    clearCanvas()
    ctx.value.drawImage(img, 0, 0, drawCanvas.value.width / window.devicePixelRatio, drawCanvas.value.height / window.devicePixelRatio)
  }
  img.src = history.stack[index]
  history.pointer = index
}

// Drawing event handlers
function startDraw(e) {
  if (mode.value !== 'draw') return
  isDrawing.value = true
  const point = getPoint(e)
  lastPoint.value = point
  ctx.value.beginPath()
  ctx.value.moveTo(point.x, point.y)
  ctx.value.strokeStyle = color.value
  ctx.value.lineWidth = penSize.value
}

function draw(e) {
  if (!isDrawing.value || mode.value !== 'draw') return
  const point = getPoint(e)
  ctx.value.lineTo(point.x, point.y)
  ctx.value.stroke()
  lastPoint.value = point
}

function endDraw() {
  if (!isDrawing.value) return
  isDrawing.value = false
  ctx.value.closePath()
  pushHistory()
}

function getPoint(e) {
  const canvas = drawCanvas.value
  if (!canvas) return { x: 0, y: 0 }
  const rect = canvas.getBoundingClientRect()
  let clientX, clientY
  if (e.touches && e.touches[0]) {
    clientX = e.touches[0].clientX
    clientY = e.touches[0].clientY
  } else {
    clientX = e.clientX
    clientY = e.clientY
  }
  return {
    x: (clientX - rect.left),
    y: (clientY - rect.top),
  }
}

function undo() {
  if (!canUndo.value) return
  restoreHistory(history.pointer - 1)
}

function redo() {
  if (!canRedo.value) return
  restoreHistory(history.pointer + 1)
}

function clearPad() {
  if (mode.value === 'draw') {
    // Clear canvas and history only in draw mode
    clearCanvas()
    history.stack = []
    history.pointer = -1
    pushHistory()
  } else {
    // Clear typed text only in type mode
    typedText.value = ''
  }
}

// For typed mode font select
function selectFont(i) {
  selectedFontIndex.value = i
}

function resetAll() {
  clearPad()
  typedText.value = ''
  fontSize.value = 52
  selectedFontIndex.value = 0
}

async function downloadSignature() {
  if (mode.value === 'draw') {
    if (!drawCanvas.value) return alert('Please draw your signature first.')
    // Use html2canvas for better screenshot, but here canvas is direct
    const dataUrl = drawCanvas.value.toDataURL('image/png')
    triggerDownload(dataUrl, 'signature.png')
  } else {
    // Create temporary canvas to render typed text
    const w = 800, h = 200
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = w
    tempCanvas.height = h
    const tempCtx = tempCanvas.getContext('2d')
    tempCtx.fillStyle = 'rgba(0,0,0,0)'
    tempCtx.fillRect(0, 0, w, h)
    tempCtx.fillStyle = color.value
    tempCtx.textBaseline = 'middle'
    tempCtx.textAlign = 'center'
    tempCtx.font = `${fontSize.value}px ${fonts[selectedFontIndex.value].cssName}`
    tempCtx.fillText(typedText.value || 'Signature', w / 2, h / 2)
    const dataUrl = tempCanvas.toDataURL('image/png')
    triggerDownload(dataUrl, 'signature-typed.png')
  }
}

function triggerDownload(dataUrl, filename) {
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = filename
  a.click()
}

// Function to use signature in PdfEditor
async function useSignature() {
  let dataUrl = ''
  let signatureState = {
    mode: mode.value,
    color: color.value,
    image: ''
  }
  
  if (mode.value === 'draw') {
    if (!drawCanvas.value) {
      alert('Please draw your signature first.')
      return
    }
    dataUrl = drawCanvas.value.toDataURL('image/png')
    signatureState.image = dataUrl
  } else {
    // Type mode - save text data
    if (!typedText.value || typedText.value.trim() === '') {
      alert('Please enter your name first.')
      return
    }
    
    // Create image from text
    const w = 800, h = 200
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = w
    tempCanvas.height = h
    const tempCtx = tempCanvas.getContext('2d')
    tempCtx.fillStyle = 'rgba(0,0,0,0)'
    tempCtx.fillRect(0, 0, w, h)
    tempCtx.fillStyle = color.value
    tempCtx.textBaseline = 'middle'
    tempCtx.textAlign = 'center'
    tempCtx.font = `${fontSize.value}px ${fonts[selectedFontIndex.value].cssName}`
    tempCtx.fillText(typedText.value, w / 2, h / 2)
    dataUrl = tempCanvas.toDataURL('image/png')
    
    // Save type mode data
    signatureState.image = dataUrl
    signatureState.text = typedText.value
    signatureState.fontSize = fontSize.value
    signatureState.fontIndex = selectedFontIndex.value
  }
  
  // Emit complete signature state to parent component
  emit('signed', signatureState)
}

// Function to navigate to PDF Editor with current signature
async function navigateToPdfEditor() {
  let dataUrl = ''
  let signatureState = {
    mode: mode.value,
    color: color.value,
    image: ''
  }
  
  if (mode.value === 'draw') {
    if (!drawCanvas.value) {
      alert('Please draw your signature first.')
      return
    }
    dataUrl = drawCanvas.value.toDataURL('image/png')
    signatureState.image = dataUrl
  } else {
    // Type mode - save text data
    if (!typedText.value || typedText.value.trim() === '') {
      alert('Please enter your name first.')
      return
    }
    const w = 800, h = 200
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = w
    tempCanvas.height = h
    const tempCtx = tempCanvas.getContext('2d')
    tempCtx.fillStyle = 'rgba(0,0,0,0)'
    tempCtx.fillRect(0, 0, w, h)
    tempCtx.fillStyle = color.value
    tempCtx.textBaseline = 'middle'
    tempCtx.textAlign = 'center'
    tempCtx.font = `${fontSize.value}px ${fonts[selectedFontIndex.value].cssName}`
    tempCtx.fillText(typedText.value, w / 2, h / 2)
    dataUrl = tempCanvas.toDataURL('image/png')
    
    // Save type mode data
    signatureState.image = dataUrl
    signatureState.text = typedText.value
    signatureState.fontSize = fontSize.value
    signatureState.fontIndex = selectedFontIndex.value
  }
  
  // Emit navigation event with complete signature state
  emit('navigate-to-editor', signatureState)
}

onMounted(() => {
  initCanvas()
  window.addEventListener('resize', initCanvas)
  
  // Restore existing signature if provided
  if (props.existingSignature) {
    setTimeout(() => {
      // Check if it's an object (new format) or string (old format/image only)
      if (typeof props.existingSignature === 'object') {
        const state = props.existingSignature
        
        // Restore mode
        mode.value = state.mode || 'draw'
        color.value = state.color || '#000000'
        
        if (state.mode === 'type' && state.text) {
          // Restore type mode data
          typedText.value = state.text
          fontSize.value = state.fontSize || 40
          selectedFontIndex.value = state.fontIndex || 0
        } else if (state.mode === 'draw' && state.image && drawCanvas.value) {
          // Restore draw mode - load image to canvas
          const img = new Image()
          img.onload = () => {
            if (ctx.value && drawCanvas.value) {
              ctx.value.clearRect(0, 0, drawCanvas.value.width, drawCanvas.value.height)
              const rect = drawCanvas.value.getBoundingClientRect()
              ctx.value.drawImage(img, 0, 0, rect.width, rect.height)
              pushHistory()
            }
          }
          img.src = state.image
        }
      } else if (typeof props.existingSignature === 'string' && drawCanvas.value) {
        // Old format - just image string
        const img = new Image()
        img.onload = () => {
          if (ctx.value && drawCanvas.value) {
            ctx.value.clearRect(0, 0, drawCanvas.value.width, drawCanvas.value.height)
            const rect = drawCanvas.value.getBoundingClientRect()
            ctx.value.drawImage(img, 0, 0, rect.width, rect.height)
            pushHistory()
          }
        }
        img.src = props.existingSignature
      }
    }, 100)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', initCanvas)
})
</script>

<style scoped>
/* Custom scrollbar for font grid */
.font-grid::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.font-grid::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.font-grid::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
  transition: background 0.2s;
}

.font-grid::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Custom scrollbar for font preview */
.type-input::-webkit-scrollbar {
  height: 6px;
}

.type-input::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.type-input::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.type-input::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.signature-wrapper {
  max-width: 1024px;
  max-height: 800px;
  margin: 0 auto;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
}

.card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 4px;
  padding-bottom: 9px;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.left-controls {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.color-section,
.brush-section {
  display: flex;
  gap: 12px;
  align-items: center;
}

.brush-section {
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.label {
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
}

.color-palette {
  display: flex;
  gap: 8px;
}

.color-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.color-dot:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.color-dot.selected {
  border-color: #fff;
  box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.08);
}

.tick {
  color: white;
  opacity: 1;
  font-weight: 700;
}

.divider {
  width: 1px;
  height: 28px;
  background: #e5e7eb;
}

.slider {
  width: 100px;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2ecc71;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
  transition: all 0.2s ease;
}

.slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 4px 8px rgba(37, 99, 235, 0.4);
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2ecc71;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
  transition: all 0.2s ease;
}

.size-value {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  min-width: 40px;
}

.mode-toggle {
  display: flex;
  gap: 8px;
  background: #f9fafb;
  padding: 6px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.mode-btn {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: #666;
  transition: all 0.2s ease;
}

.mode-btn:hover {
  background: white;
  color: #2ecc71;
}

.mode-btn.active {
  background: white;
  color: #2ecc71;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.icon-inline {
  width: 16px;
  height: 16px;
}

.left-controls {
  display: flex;
  gap: 12px;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.2s ease;
}

.icon-btn:hover:not(:disabled) {
  background: #f9fafb;
  color: #1a1a1a;
  border-color: #d1d5db;
}

.icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.icon-btn.danger {
  color: #dc2626;
}

.icon-btn.danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fecaca;
}

.editor-area {
  padding: 12px 0;
}

.draw-area {
  position: relative;
  height: 280px;
  border-radius: 8px;
  background: white;
  border: 2px solid #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease;
}

.draw-area:hover {
  border-color: #d1d5db;
}

.draw-canvas {
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
  cursor: crosshair;
}

.bottom-line {
  position: absolute;
  bottom: 20px;
  left: 40px;
  right: 40px;
  height: 2px;
  background: #e5e7eb;
  border-radius: 1px;
}

.type-area {
  padding: 6px;
}

.type-input {
  width: 100%;
  padding: 10px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
  font-family: inherit;
  margin-bottom: 16px;
}

.type-input:focus {
  border-color: #2ecc71;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  outline: none;
}

.font-size-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.font-preview {
  text-align: center;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.font-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-height: 250px;
  overflow-y: auto;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
}

.font-card {
  border: 2px solid #e5e7eb;
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  transition: all 0.2s ease;
}

.font-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.font-card.selected {
  border-color: #2ecc71;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.font-sample {
  display: inline-block;
  text-align: center;
  white-space: nowrap;
}

.actions {
  display: flex;
  gap: 14px;
  justify-content: center;
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #2ecc71;
  color: white;
}

.btn-primary:hover {
  background: #24bd64ea;
  box-shadow: 0 4px 12px rgba(37, 235, 156, 0.3);
}

.btn-secondary {
  background: white;
  color: #666;
  border: 1px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f9fafb;
  color: #1a1a1a;
  border-color: #d1d5db;
}

@media (max-width: 768px) {
  .signature-wrapper {
    padding: 16px 12px;
  }

  .card {
    padding: 16px;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  /* Bring mode toggle to the top for easier access */
  .mode-toggle {
    width: 100%;
    order: -1;
  }

  .mode-btn {
    flex: 1;
    justify-content: center;
  }

  /* First group of controls (Colors, Sizes) */
  .toolbar > .left-controls:first-child {
    flex-direction: column;
    width: 100%;
    gap: 12px;
    align-items: stretch;
  }

  .color-section {
    width: 100%;
    justify-content: center;
    gap: 24px;
  }

  .color-palette {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  /* Hide divider on mobile */
  .divider {
    display: none;
  }

  /* Brush and Font size controls */
  .brush-section, 
  .font-size-controls {
    width: 100%;
    justify-content: space-between;
    box-sizing: border-box;
  }

  .slider {
    flex: 1;
    width: auto;
    margin: 0 12px;
  }

  /* Undo/Redo controls */
  .toolbar > .left-controls:last-child {
    width: 100%;
    justify-content: center;
    gap: 16px;
  }

  .draw-area {
    height: 240px;
  }

  .actions {
    flex-direction: column;
    gap: 12px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }

  .font-grid {
    grid-template-columns: 1fr;
  }
  
  .font-preview {
    padding: 10px;
  }
}
</style>